import { useEffect, useRef, useState } from 'react';
import { ArrowUp, Mic, Square, Upload } from 'lucide-react';
import type { AgentSession, Message } from '@/lib/agents/types';
import { useSessionStore } from '@/lib/store/sessionStore';
import { useUIStore } from '@/lib/store/uiStore';
import { shortId, cn } from '@/lib/utils';
import { chord } from '@/lib/platform';
import { parseSlash, runSlashCommand } from '@/lib/commands/handlers';
import { runStream, cancelStream } from '@/lib/runStream';
import {
  filesToAttachments,
  folderEntriesFromInput,
  filesFromClipboard,
} from '@/lib/attachments';
import { ModelMenu } from './ModelMenu';
import { EffortMenu } from './EffortMenu';
import { PlusMenu } from './PlusMenu';
import { ModeMenu } from './ModeMenu';
import { PermissionMenu } from './PermissionMenu';
import { SlashPalette } from './SlashPalette';
import { AttachmentChips } from './AttachmentChips';
import { StatusLine } from '../thread/StatusLine';

interface Props {
  session: AgentSession | undefined;
}

export function Composer({ session }: Props) {
  const setModel = useSessionStore((s) => s.setModel);
  const setEffort = useSessionStore((s) => s.setEffort);
  const appendMessage = useSessionStore((s) => s.appendMessage);
  const addAttachments = useSessionStore((s) => s.addAttachments);
  const removeAttachment = useSessionStore((s) => s.removeAttachment);

  const setSlashOpen = useUIStore((s) => s.setSlashOpen);
  const pendingInsert = useUIStore((s) => s.pendingComposerInsert);
  const setPendingInsert = useUIStore((s) => s.setPendingComposerInsert);

  const [value, setValue] = useState('');

  // Drain one-shot inserts (e.g. select-element references from the Preview pane).
  useEffect(() => {
    if (!pendingInsert) return;
    setValue((v) => (v ? `${v}\n${pendingInsert}` : pendingInsert));
    setPendingInsert(null);
    queueMicrotask(() => taRef.current?.focus());
  }, [pendingInsert, setPendingInsert]);
  const [dragOver, setDragOver] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  // auto-grow
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 240) + 'px';
  }, [value]);

  // ⌘K opens slash palette
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isSlashTrigger = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (isSlashTrigger) {
        e.preventDefault();
        setSlashOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setSlashOpen]);

  const sendUserMessageAndStream = (sessionId: string, text: string) => {
    const msg: Message = {
      id: shortId('msg'),
      role: 'user',
      createdAt: Date.now(),
      parts: [{ type: 'text', text }],
    };
    appendMessage(sessionId, msg);
    runStream(sessionId);
  };

  const onSend = () => {
    if (!session || !value.trim()) return;
    const text = value.trim();

    // slash parsing
    const parsed = parseSlash(text);
    if (parsed) {
      const result = runSlashCommand(parsed.name, parsed.args, {
        attachFile: () => fileInputRef.current?.click(),
        attachFolder: () => folderInputRef.current?.click(),
      });
      if (result.kind === 'consumed') { setValue(''); return; }
      if (result.kind === 'consumed_then_send') {
        // newSession may have switched activeId — read current and stream there
        const { activeId } = useSessionStore.getState();
        if (activeId) sendUserMessageAndStream(activeId, result.remaining);
        setValue('');
        return;
      }
      if (result.kind === 'error') {
        appendMessage(session.id, {
          id: shortId('msg'),
          role: 'system',
          createdAt: Date.now(),
          parts: [{ type: 'text', text: result.message }],
        });
        setValue('');
        return;
      }
      // 'unknown' — fall through and send as a normal message
    }

    sendUserMessageAndStream(session.id, text);
    setValue('');
  };

  const onStop = () => {
    if (session) cancelStream(session.id);
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (e.key === '/' && value === '') {
      e.preventDefault();
      setSlashOpen(true);
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const onPickSlash = (name: string) => {
    setValue((v) => (v ? v + ` /${name} ` : `/${name} `));
    setTimeout(() => taRef.current?.focus(), 30);
  };

  // ---- attachment handlers ----
  const onFiles = async (files: FileList | File[] | null) => {
    if (!session || !files || (files as FileList).length === 0) return;
    const items = await filesToAttachments(files);
    if (items.length) addAttachments(session.id, items);
  };

  const onFolderPicked = (files: FileList | null) => {
    if (!session || !files || files.length === 0) return;
    const items = folderEntriesFromInput(files);
    if (items.length) addAttachments(session.id, items);
  };

  const onPaste: React.ClipboardEventHandler<HTMLTextAreaElement> = (e) => {
    const native = e.nativeEvent as ClipboardEvent;
    const files = filesFromClipboard(native);
    if (files.length) {
      e.preventDefault();
      onFiles(files);
    }
  };

  const onDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
    if (Array.from(e.dataTransfer.types).includes('Files')) {
      e.preventDefault();
      setDragOver(true);
    }
  };
  const onDragLeave: React.DragEventHandler<HTMLDivElement> = () => setDragOver(false);
  const onDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) {
      onFiles(e.dataTransfer.files);
    }
  };

  return (
    <div className="border-t border-border bg-canvas">
      <div className="mx-auto w-full max-w-[760px] px-6 pb-3 pt-2">
        <StatusLine session={session} />

        {session && (
          <AttachmentChips
            attachments={session.attachments}
            onRemove={(id) => removeAttachment(session.id, id)}
          />
        )}

        <div
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          className={cn(
            'relative rounded-xl bg-surface shadow-composer transition-shadow',
            dragOver && 'ring-2 ring-accent/60'
          )}
        >
          {dragOver && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-accent/5 text-[13px] font-medium text-accent">
              <Upload className="mr-1.5 h-4 w-4" />
              Drop to attach
            </div>
          )}

          <textarea
            ref={taRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            placeholder={
              session
                ? `Message ${session.name}…  (/ for commands, ${chord('mod', 'K')} for palette)`
                : 'No session.'
            }
            disabled={!session}
            rows={1}
            className="block w-full resize-none bg-transparent px-3.5 pb-2 pt-3 text-[14px] leading-relaxed text-fg placeholder:text-fg-subtle outline-none"
          />

          <div className="flex items-center gap-1 px-2 pb-2 pt-1">
            <PlusMenu
              onOpenSlash={() => setSlashOpen(true)}
              onPickFile={() => fileInputRef.current?.click()}
              onPickFolder={() => folderInputRef.current?.click()}
            />

            <PermissionMenu />

            <button
              type="button"
              className="focus-ring flex h-7 w-7 items-center justify-center rounded-md text-fg-muted hover:bg-surface-2 hover:text-fg"
              aria-label="Voice input"
              title="Voice input (coming soon)"
            >
              <Mic className="h-3.5 w-3.5" />
            </button>

            <span className="mx-1 h-4 w-px bg-border" />

            <ModeMenu />

            <span className="mx-1 h-4 w-px bg-border" />

            {session && (
              <>
                <ModelMenu
                  modelId={session.modelId}
                  onSelect={(id) => setModel(session.id, id)}
                />
                <EffortMenu
                  effort={session.effort}
                  modelId={session.modelId}
                  onSelect={(e) => setEffort(session.id, e)}
                />
              </>
            )}

            <SendButton
              disabled={!session || (!value.trim() && !isActive(session?.status))}
              streaming={isActive(session?.status)}
              onSend={onSend}
              onStop={onStop}
            />
          </div>
        </div>

        <div className="mt-1.5 flex items-center justify-between px-1 text-2xs text-fg-subtle">
          <div className="flex items-center gap-3">
            <span>
              <kbd>↵</kbd> send · <kbd>{chord('shift', '↵')}</kbd> newline · <kbd>{chord('mod', 'K')}</kbd> commands · <kbd>{chord('mod', 'shift', 'N')}</kbd> new
            </span>
          </div>
          <span>{value.length} chars</span>
        </div>
      </div>

      {/* hidden file pickers */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }}
      />
      <input
        ref={folderInputRef}
        type="file"
        hidden
        // @ts-expect-error: webkitdirectory is non-standard but supported in Chromium/Safari
        webkitdirectory=""
        directory=""
        multiple
        onChange={(e) => { onFolderPicked(e.target.files); e.target.value = ''; }}
      />

      <SlashPalette onPick={onPickSlash} />
    </div>
  );
}

function isActive(status: string | undefined): boolean {
  return status === 'thinking' || status === 'streaming' || status === 'tool_calling';
}

function SendButton({
  disabled,
  streaming,
  onSend,
  onStop,
}: {
  disabled: boolean;
  streaming: boolean;
  onSend: () => void;
  onStop: () => void;
}) {
  if (streaming) {
    return (
      <button
        type="button"
        onClick={onStop}
        className="focus-ring ml-auto flex h-7 w-7 items-center justify-center rounded-md bg-fg text-canvas hover:bg-fg/90"
        aria-label="Stop"
      >
        <Square className="h-3.5 w-3.5" />
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onSend}
      disabled={disabled}
      className="focus-ring ml-auto flex h-7 w-7 items-center justify-center rounded-md bg-accent text-accent-fg transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-30"
      aria-label="Send"
    >
      <ArrowUp className="h-3.5 w-3.5" />
    </button>
  );
}
